import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
} from 'react';
import { CellState, formatTime } from '../types';
import type { PuzzleData } from '../types';
import { validate, cluesFromLine, cluesFromGrid } from '@org/nonogram-engine';
import { ParallaxBg } from './ParallaxBg';
import { Stars, WinPopup, Button, Title, Chip, Muted, TextButton, PillButton } from './ui';
import { BoardGrid } from './BoardGrid';
import type { BoardGridHandle, BoardParticle } from './BoardGrid';
import { useGameStore } from '../store';
import {
  playFill,
  playMark,
  playUndo,
  playLineComplete,
  playWin,
  playError,
  playModeSwitch,
  playClick,
} from '../sounds';
import { SharePopup } from './SharePopup';
import './Game.css';

/* ---------- helpers ---------- */

function calcCellSize(totalCols: number, totalRows: number): number {
  const rootStyle = getComputedStyle(document.documentElement);
  const readCssPx = (name: string, fallback: number) => {
    const raw = rootStyle.getPropertyValue(name).trim();
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? value : fallback;
  };
  // Board has padding (--space-md each side) + 2px border each side
  const spaceMd = Math.min(16, Math.max(10, window.innerWidth * 0.025));
  const boardChrome = (spaceMd + 2) * 2;
  const appMinWidth = readCssPx('--app-min-width', 360);
  const minBoardPixels = readCssPx('--game-board-min-size', 336);
  // App container is capped at --app-max-width (728px)
  const containerW = Math.min(
    Math.max(window.innerWidth, appMinWidth, minBoardPixels + boardChrome),
    728
  );
  const maxW = containerW - boardChrome;
  // Vertical: topbar + title + board margin + board chrome + controls + hint
  const maxH = window.innerHeight - boardChrome - 200;
  // First pass without gaps
  let size = Math.floor(Math.min(maxW / totalCols, maxH / totalRows));
  // Subtract gaps (gap = max(1, round(cellSize * 0.03)))
  const gap = Math.max(1, Math.round(size * 0.03));
  size = Math.floor(
    Math.min(
      (maxW - (totalCols - 1) * gap) / totalCols,
      (maxH - (totalRows - 1) * gap) / totalRows
    )
  );
  const minSizeForBoard = Math.ceil(
    Math.max(minBoardPixels / totalCols, minBoardPixels / totalRows)
  );
  return Math.min(52, Math.max(18, minSizeForBoard, size));
}

function announce(msg: string) {
  const el = document.getElementById('sr-announce');
  if (!el) return;
  el.textContent = '';
  requestAnimationFrame(() => {
    el.textContent = msg;
  });
}

function cloneGrid(g: CellState[][]): CellState[][] {
  return g.map((r) => [...r]);
}

/**
 * Determines which clues are satisfied based on terminated runs.
 * A run is "terminated" when followed by a mark (X) or the edge.
 * Returns a boolean array where each index matches the clue index.
 */
function getCompletedClues(line: CellState[], clues: number[]): boolean[] {
  const result = new Array(clues.length).fill(false);
  if (clues.length === 0 || (clues.length === 1 && clues[0] === 0)) {
    // No clues — all done if no filled cells
    return [line.every((c) => c !== CellState.Filled)];
  }

  // Find terminated runs: sequences of filled cells followed by mark or edge
  const terminatedRuns: number[] = [];
  let currentRun = 0;

  for (let i = 0; i <= line.length; i++) {
    const cell = i < line.length ? line[i] : null;

    if (cell === CellState.Filled) {
      currentRun++;
    } else {
      if (currentRun > 0) {
        // Run ended — check if terminated (by mark or edge)
        const isTerminated = cell === CellState.Marked || cell === null;
        if (isTerminated) {
          terminatedRuns.push(currentRun);
        }
      }
      currentRun = 0;
    }
  }

  // Match terminated runs to clues in order
  for (let i = 0; i < Math.min(terminatedRuns.length, clues.length); i++) {
    if (terminatedRuns[i] === clues[i]) {
      result[i] = true;
    } else {
      // Mismatch — stop marking clues as complete
      break;
    }
  }

  return result;
}

/* ---------- types ---------- */

const PARTICLE_COLORS = ['#ffe080', '#f0b830', '#ffda68', '#c08010', '#d89818'];

interface DragState {
  action: 'fill' | 'unfill' | 'mark' | 'unmark';
  axis: 'row' | 'col' | null;
  startRow: number;
  startCol: number;
  lastRow: number;
  lastCol: number;
  cells: Set<string>;
}

export interface GameProps {
  name: string;
  puzzle: PuzzleData;
  storageKey?: string;
  resumeFromSave?: boolean;
  mode: 'tutorial' | 'levels' | 'train';
  embedded?: boolean;
  // Settings — all passed via props (parents read from store or override for tutorial)
  strict?: boolean;
  /** Hint count: 0 = disabled, -1 = unlimited, positive = limited */
  hintCount?: number;
  autoMark?: boolean;
  /** Validate count: 0 = disabled, -1 = unlimited, positive = limited */
  validateCount?: number;
  timerVisible?: boolean;
  cellStateCycleEnabled?: boolean;
  spanHintsEnabled?: boolean;
  hintTrigger?: number;
  onBack: () => void;
  onNext?: () => void;
  onWin?: () => void;
  onGridDirty?: (dirty: boolean) => void;
  resetTrigger?: number;
  onFreshEntryConsumed?: () => void;
  /** If provided, a share button is shown. */
  onShare?: (opts: { solution: boolean[][]; grid?: CellState[][] }) => Promise<'shared' | 'copied' | 'failed'>;
  /** Pre-populated grid from a share URL. */
  initialProgress?: CellState[][];
  /** Read-only view of a completed puzzle: board starts fully solved, no
   *  timer, no interaction, no saves, no win popup. */
  viewMode?: boolean;
  /** When in view mode, display this elapsed time instead of 0. */
  viewElapsed?: number;
}

/* ---------- component ---------- */

export function Game({
  name,
  puzzle,
  storageKey,
  resumeFromSave = false,
  mode,
  embedded,
  strict,
  hintCount = 3,
  autoMark,
  validateCount = -1,
  timerVisible,
  cellStateCycleEnabled,
  spanHintsEnabled,
  hintTrigger: externalHintTrigger,
  onBack,
  onNext,
  onWin,
  onGridDirty,
  resetTrigger,
  onFreshEntryConsumed,
  onShare,
  initialProgress,
  viewMode = false,
  viewElapsed,
}: GameProps) {
  const [localHintTrigger, setLocalHintTrigger] = useState(0);
  const hintTrigger = externalHintTrigger ?? localHintTrigger;
  // -1 means unlimited, so we start with the count (or Infinity for unlimited)
  const [hintsRemaining, setHintsRemaining] = useState(() =>
    hintCount < 0 ? Infinity : hintCount
  );
  const [validationsRemaining, setValidationsRemaining] = useState(() =>
    validateCount < 0 ? Infinity : validateCount
  );
  const { width, height } = puzzle;

  const puzzleClues = useMemo(() => cluesFromGrid(puzzle.solution), [puzzle]);
  const rowClues = puzzleClues.rows;
  const colClues = puzzleClues.cols;

  const clearCategorySave = useGameStore((s) => s.clearCategorySave);
  const autoPauseEnabled = useGameStore((s) => s.autoPauseEnabled);

  const autoCross = autoMark ?? false;
  const showSpanHints = (spanHintsEnabled ?? false) && mode !== 'tutorial';
  const maxColClueLen = Math.max(...colClues.map((c) => c.length));
  const maxRowClueLen = Math.max(...rowClues.map((c) => c.length));
  const spanExtra = showSpanHints ? 1 : 0;
  const totalCols = maxRowClueLen + spanExtra + width;
  const totalRows = maxColClueLen + spanExtra + height;

  /* --- state --- */

  const [cellSize, setCellSize] = useState(() =>
    calcCellSize(totalCols, totalRows)
  );

  const [grid, setGrid] = useState<CellState[][]>(() => {
    if (viewMode) {
      return puzzle.solution.map((row) =>
        row.map((filled) => (filled ? CellState.Filled : CellState.Empty))
      );
    }
    if (initialProgress && initialProgress.length === height && initialProgress[0]?.length === width) {
      return initialProgress.map((r) => [...r]);
    }
    if (storageKey && resumeFromSave) {
      const saved = useGameStore.getState().loadGameState(storageKey);
      if (saved) return saved.grid.map((r) => r.map((c) => c as CellState));
    }
    return Array.from({ length: height }, () =>
      Array(width).fill(CellState.Empty)
    );
  });

  const [solved, setSolved] = useState(viewMode);
  const [elapsed, setElapsed] = useState(() => {
    if (viewMode) return viewElapsed ?? 0;
    return storageKey && resumeFromSave
      ? useGameStore.getState().loadGameState(storageKey)?.elapsed ?? 0
      : 0;
  });
  const [showWin, setShowWin] = useState(false);
  const [particles, setParticles] = useState<BoardParticle[]>([]);

  const boardHandleRef = useRef<BoardGridHandle>(null);
  const [focusRow, setFocusRow] = useState(-1);
  const [focusCol, setFocusCol] = useState(-1);

  const [touchMode, setTouchMode] = useState<'fill' | 'mark' | 'hint'>('fill');
  const [paused, setPaused] = useState(false);
  const emptyNumSet = useMemo(() => new Set<number>(), []);
  const [lastAction, setLastAction] = useState({
    row: -1,
    col: -1,
    type: '',
    id: 0,
  });
  const [errorCells, setErrorCells] = useState<Set<string>>(new Set());
  const [mistakes, setMistakes] = useState(0);
  const [validFlash, setValidFlash] = useState(0); // 0-1 intensity (1 = full green)
  const [validFlashCells, setValidFlashCells] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);
  const [showSharePopup, setShowSharePopup] = useState(false);

  /* --- notify parent when grid has any non-empty cells --- */

  useEffect(() => {
    if (!onGridDirty) return;
    if (viewMode) {
      onGridDirty(false);
      return;
    }
    const dirty = grid.some((row) => row.some((c) => c !== CellState.Empty));
    onGridDirty(dirty);
  }, [grid, onGridDirty, viewMode]);

  /* --- refs --- */

  const undoRef = useRef<CellState[][][]>([]);
  const redoRef = useRef<CellState[][][]>([]);
  const preActionRef = useRef<CellState[][] | null>(null);
  const gridRef = useRef(grid);
  const solvedRef = useRef(viewMode);
  const elapsedRef = useRef(elapsed);
  const dragRef = useRef<DragState | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const particleColors = PARTICLE_COLORS;
  const freshEntryConsumedRef = useRef(false);
  const hasTouch =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  /* --- hover crosshair (delegated to BoardGrid via imperative handle) --- */

  const updateHover = useCallback((row: number, col: number) => {
    boardHandleRef.current?.setHover(row, col);
  }, []);

  /* --- computed --- */

  const totalNeeded = useMemo(() => {
    let c = 0;
    for (let r = 0; r < height; r++)
      for (let col = 0; col < width; col++) if (puzzle.solution[r][col]) c++;
    return c;
  }, [puzzle, width, height]);

  const currentFilled = useMemo(() => {
    let c = 0;
    for (let r = 0; r < height; r++)
      for (let col = 0; col < width; col++)
        if (grid[r][col] === CellState.Filled) c++;
    return c;
  }, [grid, width, height]);

  /* --- pure helpers --- */

  const checkWin = useCallback(
    (g: CellState[][]): boolean => {
      const boolGrid = g.map((row) => row.map((c) => c === CellState.Filled));
      return validate(boolGrid, puzzleClues);
    },
    [puzzleClues]
  );

  const isRowDone = useCallback(
    (row: number, g: CellState[][]): boolean => {
      const actual = cluesFromLine(g[row].map((c) => c === CellState.Filled));
      const expected = rowClues[row];
      return (
        actual.length === expected.length &&
        actual.every((v, i) => v === expected[i])
      );
    },
    [rowClues]
  );

  const isColDone = useCallback(
    (col: number, g: CellState[][]): boolean => {
      const actual = cluesFromLine(
        g.map((row) => row[col] === CellState.Filled)
      );
      const expected = colClues[col];
      return (
        actual.length === expected.length &&
        actual.every((v, i) => v === expected[i])
      );
    },
    [colClues]
  );

  const completedRows = useMemo(
    () => Array.from({ length: height }, (_, r) => isRowDone(r, grid)),
    [height, grid, isRowDone]
  );

  const completedCols = useMemo(
    () => Array.from({ length: width }, (_, c) => isColDone(c, grid)),
    [width, grid, isColDone]
  );

  // Per-clue completion for auto-mark feature (gray out satisfied clues)
  const rowClueDone = useMemo(
    () => (autoCross ? grid.map((row, r) => getCompletedClues(row, rowClues[r])) : undefined),
    [grid, rowClues, autoCross]
  );

  const colClueDone = useMemo(
    () =>
      autoCross
        ? Array.from({ length: width }, (_, c) =>
            getCompletedClues(
              grid.map((row) => row[c]),
              colClues[c]
            )
          )
        : undefined,
    [grid, colClues, width, autoCross]
  );

  /* --- grid updater (keeps ref in sync) --- */

  const updateGrid = useCallback(
    (next: CellState[][]) => {
      gridRef.current = next;
      setGrid(next);
      if (storageKey && !solvedRef.current) {
        useGameStore
          .getState()
          .saveGameState(storageKey, cloneGrid(next), elapsedRef.current);
      }
    },
    [storageKey]
  );

  /* --- sync refs --- */

  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);
  useEffect(() => {
    solvedRef.current = solved;
  }, [solved]);

  /* --- effects --- */

  useEffect(() => {
    if (!storageKey || resumeFromSave || viewMode) return;
    clearCategorySave(storageKey);
  }, [clearCategorySave, resumeFromSave, storageKey, viewMode]);

  useEffect(() => {
    if (
      resumeFromSave ||
      viewMode ||
      !onFreshEntryConsumed ||
      freshEntryConsumedRef.current
    ) {
      return;
    }
    freshEntryConsumedRef.current = true;
    onFreshEntryConsumed();
  }, [onFreshEntryConsumed, resumeFromSave, viewMode]);

  useEffect(() => {
    const handler = () => setCellSize(calcCellSize(totalCols, totalRows));
    handler();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [totalCols, totalRows]);

  useEffect(() => {
    if (solved || paused) return;
    const id = setInterval(() => setElapsed((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [solved, paused]);

  /* Auto-pause when tab loses visibility */
  useEffect(() => {
    if (!autoPauseEnabled) return;
    const handler = () => {
      if (document.hidden && !solvedRef.current) {
        setPaused(true);
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [autoPauseEnabled]);

  /* Save the latest timer state when leaving the puzzle */
  useEffect(() => {
    if (!storageKey) return;
    return () => {
      const hasProgress = gridRef.current.some((row) =>
        row.some((cell) => cell !== CellState.Empty)
      );
      if (!solvedRef.current) {
        const store = useGameStore.getState();
        if (hasProgress) {
          store.saveGameState(
            storageKey,
            cloneGrid(gridRef.current),
            elapsedRef.current
          );
        } else {
          store.clearGameState(storageKey);
        }
      }
    };
  }, [storageKey]);

  useEffect(() => {
    announce(`${name}. ${width} by ${height} grid.`);
  }, [name, width, height]);

  /* --- line completion flash --- */

  const checkLines = useCallback(
    (before: CellState[][], after: CellState[][]): CellState[][] => {
      const rows = new Set<number>();
      const cols = new Set<number>();
      for (let r = 0; r < height; r++)
        if (!isRowDone(r, before) && isRowDone(r, after)) rows.add(r);
      for (let c = 0; c < width; c++)
        if (!isColDone(c, before) && isColDone(c, after)) cols.add(c);
      if (rows.size > 0 || cols.size > 0) {
        playLineComplete();
      }
      return after;
    },
    [width, height, isRowDone, isColDone]
  );

  /* --- win handler --- */

  const triggerWin = useCallback(() => {
    setSolved(true);
    solvedRef.current = true;
    if (storageKey) {
      const store = useGameStore.getState();
      store.savePuzzleCompletion(
        storageKey,
        elapsedRef.current,
        3 - hintsRemaining
      );
      store.clearGameState(storageKey);
    }
    onWin?.();
    playWin();
    announce('Puzzle solved!');

    const cs = calcCellSize(totalCols, totalRows);
    const g = Math.max(1, Math.round(cs * 0.03));
    setTimeout(() => {
      const ps: Particle[] = [];
      let pid = 0;
      for (let r = 0; r < height; r++) {
        for (let c = 0; c < width; c++) {
          if (!puzzle.solution[r][c]) continue;
          const cx = c * (cs + g) + cs / 2;
          const cy = r * (cs + g) + cs / 2;
          for (let i = 0; i < 3; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 30 + Math.random() * 70;
            ps.push({
              id: pid++,
              x: cx,
              y: cy,
              dx: Math.cos(angle) * speed,
              dy: Math.sin(angle) * speed,
              color:
                particleColors[
                  Math.floor(Math.random() * particleColors.length)
                ],
              delay: r * 60 + c * 30,
            });
          }
        }
      }
      setParticles(ps);
    }, 100);
    setTimeout(() => setShowWin(true), 600);
  }, [
    storageKey,
    width,
    height,
    totalCols,
    totalRows,
    puzzle,
    particleColors,
    onWin,
    hintsRemaining,
  ]);

  /* --- hint (pick mode) --- */

  const applyHint = useCallback(
    (row: number, col: number) => {
      const g = gridRef.current;
      const isFilled = puzzle.solution[row][col];
      const correctState = isFilled ? CellState.Filled : CellState.Marked;
      if (g[row][col] === correctState) return false; // already correct
      const ng = cloneGrid(g);
      ng[row][col] = correctState;
      undoRef.current.push(g);
      redoRef.current = [];
      updateGrid(ng);
      if (isFilled) playFill();
      else playMark();
      setLastAction((prev) => ({
        row,
        col,
        type: isFilled ? 'fill' : 'mark',
        id: prev.id + 1,
      }));
      if (checkWin(ng)) triggerWin();
      return true;
    },
    [puzzle, updateGrid, checkWin, triggerWin]
  );

  /* --- cell toggle logic --- */

  function getPointerAction(
    cur: CellState,
    isSecondary: boolean,
    mode: 'fill' | 'mark',
    cycleEnabled: boolean
  ): 'fill' | 'unfill' | 'mark' | 'unmark' {
    if (cycleEnabled) {
      if (isSecondary || mode === 'mark') {
        if (cur === CellState.Empty) return 'mark';
        if (cur === CellState.Marked) return 'fill';
        return 'unfill';
      }
      if (cur === CellState.Empty) return 'fill';
      if (cur === CellState.Filled) return 'mark';
      return 'unmark';
    }
    if (isSecondary || mode === 'mark') {
      return cur === CellState.Marked ? 'unmark' : 'mark';
    }
    return cur === CellState.Filled ? 'unfill' : 'fill';
  }

  function applyAction(
    g: CellState[][],
    row: number,
    col: number,
    action: string
  ): CellState[][] | null {
    const cur = g[row][col];
    let next: CellState;
    if (action === 'fill' && cur === CellState.Empty) {
      if (strict && !puzzle.solution[row][col]) {
        playError();
        setMistakes((m) => m + 1);
        setErrorCells((prev) => new Set(prev).add(`${row},${col}`));
        setTimeout(
          () =>
            setErrorCells((prev) => {
              const n = new Set(prev);
              n.delete(`${row},${col}`);
              return n;
            }),
          300
        );
        // Auto-mark the wrong cell so the player remembers
        if (mode !== 'tutorial') {
          next = CellState.Marked;
        } else {
          return null;
        }
      } else {
        next = CellState.Filled;
      }
    } else if (action === 'unfill' && cur === CellState.Filled)
      next = CellState.Empty;
    else if (action === 'fill' && cur === CellState.Marked) {
      if (strict && !puzzle.solution[row][col]) {
        playError();
        setMistakes((m) => m + 1);
        setErrorCells((prev) => new Set(prev).add(`${row},${col}`));
        setTimeout(
          () =>
            setErrorCells((prev) => {
              const n = new Set(prev);
              n.delete(`${row},${col}`);
              return n;
            }),
          300
        );
        return null;
      }
      next = CellState.Filled;
    } else if (
      action === 'mark' &&
      (cur === CellState.Empty || cur === CellState.Filled)
    )
      next = CellState.Marked;
    else if (action === 'unmark' && cur === CellState.Marked)
      next = CellState.Empty;
    else return null;
    const ng = cloneGrid(g);
    ng[row][col] = next;
    return ng;
  }

  function soundForState(state: CellState) {
    if (state === CellState.Filled) playFill();
    else if (state === CellState.Marked) playMark();
  }

  function actionType(state: CellState): string {
    return state === CellState.Filled
      ? 'fill'
      : state === CellState.Marked
      ? 'mark'
      : '';
  }

  /* --- mouse handlers --- */

  const handleMouseDown = useCallback(
    (row: number, col: number, isRight: boolean) => {
      if (solvedRef.current) return;

      // Clear error only for this specific cell
      const key = `${row},${col}`;
      if (errorCells.has(key)) {
        const next = new Set(errorCells);
        next.delete(key);
        setErrorCells(next);
      }

      // Hint mode: reveal this cell's correct state, then switch back to fill
      if (touchMode === 'hint') {
        const applied = applyHint(row, col);
        if (applied) setHintsRemaining((h) => h - 1);
        setTouchMode('fill');
        return;
      }

      const g = gridRef.current;
      const action = getPointerAction(
        g[row][col],
        isRight,
        touchMode === 'mark' ? 'mark' : 'fill',
        cellStateCycleEnabled
      );

      preActionRef.current = cloneGrid(g);
      dragRef.current = {
        action,
        axis: null,
        startRow: row,
        startCol: col,
        lastRow: row,
        lastCol: col,
        cells: new Set([`${row},${col}`]),
      };

      const ng = applyAction(g, row, col, action);
      if (ng) {
        updateGrid(ng);
        const st = ng[row][col];
        setLastAction((prev) => ({
          row,
          col,
          type: actionType(st),
          id: prev.id + 1,
        }));
        soundForState(st);
      }

      setFocusRow(row);
      setFocusCol(col);
    },
    [updateGrid, touchMode, applyHint, cellStateCycleEnabled, errorCells]
  );

  const handleMouseEnter = useCallback(
    (row: number, col: number) => {
      const drag = dragRef.current;
      if (!drag || solvedRef.current) return;

      const key = `${row},${col}`;
      if (drag.cells.has(key)) return;
      drag.cells.add(key);

      let g = gridRef.current;
      const ng = applyAction(g, row, col, drag.action);
      if (ng) {
        updateGrid(ng);
        const st = ng[row][col];
        setLastAction((prev) => ({
          row,
          col,
          type: actionType(st),
          id: prev.id + 1,
        }));
        soundForState(st);
      }

      drag.lastRow = row;
      drag.lastCol = col;
    },
    [updateGrid]
  );

  const handleCellUp = useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;

    const before = preActionRef.current;
    const after = gridRef.current;
    preActionRef.current = null;

    if (before) {
      undoRef.current.push(before);
      redoRef.current = [];
      const afterCross3 = checkLines(before, after);
      if (checkWin(afterCross3)) triggerWin();
    }
  }, [checkLines, checkWin, triggerWin]);

  /* --- undo / redo / reset --- */

  const undo = useCallback(() => {
    if (solvedRef.current || paused || undoRef.current.length === 0) return;
    redoRef.current.push(gridRef.current);
    updateGrid(undoRef.current.pop()!);
    playUndo();
  }, [paused, updateGrid]);

  const redo = useCallback(() => {
    if (solvedRef.current || paused || redoRef.current.length === 0) return;
    undoRef.current.push(gridRef.current);
    updateGrid(redoRef.current.pop()!);
  }, [paused, updateGrid]);

  const reset = useCallback(() => {
    if (solvedRef.current || paused) return;
    undoRef.current.push(gridRef.current);
    redoRef.current = [];
    const empty = Array.from({ length: height }, () =>
      Array<CellState>(width).fill(CellState.Empty)
    );
    updateGrid(empty);
    setElapsed(0);
    setMistakes(0);
    if (storageKey) useGameStore.getState().clearGameState(storageKey);
  }, [width, height, paused, storageKey, updateGrid]);

  const validateGrid = useCallback(() => {
    if (solvedRef.current || paused || validationsRemaining <= 0) return;
    // Decrement validation count (unless unlimited)
    if (validationsRemaining !== Infinity) {
      setValidationsRemaining((v) => v - 1);
    }
    const g = gridRef.current;
    const errors = new Set<string>();
    for (let r = 0; r < height; r++) {
      for (let c = 0; c < width; c++) {
        const cell = g[r][c];
        const shouldBeFilled = puzzle.solution[r][c];
        // Wrong fill: cell is filled but shouldn't be
        if (cell === CellState.Filled && !shouldBeFilled) {
          errors.add(`${r},${c}`);
        }
        // Wrong X: cell is marked but should be filled
        if (cell === CellState.Marked && shouldBeFilled) {
          errors.add(`${r},${c}`);
        }
      }
    }
    if (errors.size === 0) {
      // Capture which cells to flash (only cells present now, not future additions)
      const cellsToFlash = new Set<string>();
      for (let r = 0; r < height; r++) {
        for (let c = 0; c < width; c++) {
          if (g[r][c] !== CellState.Empty) {
            cellsToFlash.add(`${r},${c}`);
          }
        }
      }
      setValidFlashCells(cellsToFlash);
      // Flash all filled cells green, then fade back to yellow (synced with toast)
      playLineComplete();
      setValidFlash(1);
      setToast('All correct!');
      setTimeout(() => setToast(null), 1500);
      // Animate fade out over 450ms (matches toast fade-out: 70%-100% of 1.5s)
      const start = performance.now();
      const duration = 450;
      const animate = () => {
        const elapsed = performance.now() - start;
        if (elapsed < duration) {
          setValidFlash(1 - elapsed / duration);
          requestAnimationFrame(animate);
        } else {
          setValidFlash(0);
          setValidFlashCells(new Set());
        }
      };
      // Hold green for ~1050ms, then fade (matches toast visible period: 15%-70% of 1.5s)
      setTimeout(() => requestAnimationFrame(animate), 1050);
    } else {
      // Show errors in red (stays until user modifies those cells)
      playError();
      setErrorCells(errors);
      setToast(`${errors.size} error${errors.size > 1 ? 's' : ''} found`);
      setTimeout(() => setToast(null), 1500);
    }
  }, [height, width, puzzle.solution, paused, validationsRemaining]);

  /* --- external reset trigger --- */

  const resetTriggerRef = useRef(resetTrigger);
  useEffect(() => {
    if (resetTrigger !== resetTriggerRef.current) {
      resetTriggerRef.current = resetTrigger;
      reset();
    }
  }, [resetTrigger, reset]);

  /* --- keyboard --- */

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (solvedRef.current || paused) return;
      const { key, ctrlKey, metaKey, shiftKey } = e;
      const mod = ctrlKey || metaKey;

      if (key === 'Escape') {
        e.preventDefault();
        onBack();
        return;
      }

      if (mod && key === 'z') {
        e.preventDefault();
        shiftKey ? redo() : undo();
        return;
      }
      if (mod && key === 'y') {
        e.preventDefault();
        redo();
        return;
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
        e.preventDefault();
        let r = focusRow < 0 ? 0 : focusRow;
        let c = focusCol < 0 ? 0 : focusCol;
        if (key === 'ArrowUp') r = Math.max(0, r - 1);
        if (key === 'ArrowDown') r = Math.min(height - 1, r + 1);
        if (key === 'ArrowLeft') c = Math.max(0, c - 1);
        if (key === 'ArrowRight') c = Math.min(width - 1, c + 1);
        setFocusRow(r);
        setFocusCol(c);
        updateHover(r, c);
        return;
      }

      if (key === ' ' || key === 'x' || key === 'X') {
        e.preventDefault();
        if (focusRow < 0 || focusCol < 0) return;
        const isRight = key === 'x' || key === 'X';
        const g = gridRef.current;
        const action = getPointerAction(
          g[focusRow][focusCol],
          isRight,
          isRight || touchMode === 'mark' ? 'mark' : 'fill',
          cellStateCycleEnabled
        );
        const ng = applyAction(g, focusRow, focusCol, action);
        if (!ng) return;

        undoRef.current.push(g);
        redoRef.current = [];
        updateGrid(ng);

        const st = ng[focusRow][focusCol];
        setLastAction((prev) => ({
          row: focusRow,
          col: focusCol,
          type: actionType(st),
          id: prev.id + 1,
        }));
        soundForState(st);

        const afterCross4 = checkLines(g, ng);
        if (checkWin(afterCross4)) triggerWin();
      }
    },
    [
      focusRow,
      focusCol,
      width,
      height,
      onBack,
      undo,
      redo,
      updateGrid,
      updateHover,
      checkLines,
      checkWin,
      triggerWin,
      touchMode,
      cellStateCycleEnabled,
      paused,
    ]
  );

  /* --- win overlay text by mode --- */

  const nextButtonText =
    mode === 'tutorial'
      ? 'Next Lesson'
      : mode === 'train'
      ? 'New Puzzle'
      : 'Next';
  const backButtonText =
    mode === 'tutorial' ? 'Lessons' : mode === 'train' ? 'Home' : 'Home';
  const showTimer = timerVisible !== false;
  const canUseHints = hintCount !== 0;
  const canValidate = validateCount !== 0;
  const canUndo = !solved && !paused && undoRef.current.length > 0;
  const canRedo = !solved && !paused && redoRef.current.length > 0;
  const toolbarStyle = {
    '--game-board-width': `${totalCols * cellSize}px`,
  } as CSSProperties;

  /* --- render --- */

  const gameContent = (
    <>
      <div className="game-board-area" style={toolbarStyle}>
        {/* Top toolbar */}
        <div
          className="game-toolbar game-toolbar--top game-toolbar--board-aligned"
          style={toolbarStyle}
        >
          <div className="game-toolbar__left">
            <PillButton
              quiet={showTimer}
              disabled={solved}
              onClick={() => {
                playClick();
                setPaused((p) => !p);
              }}
              aria-label={paused ? 'Resume puzzle' : 'Pause puzzle'}
              icon={
                paused ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="currentColor"
                  >
                    <path d="M4 2.5L11 7L4 11.5V2.5Z" />
                  </svg>
                ) : (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="currentColor"
                  >
                    <rect x="3" y="2.5" width="2.5" height="9" rx="1" />
                    <rect x="8.5" y="2.5" width="2.5" height="9" rx="1" />
                  </svg>
                )
              }
            >
              {showTimer ? formatTime(elapsed) : paused ? 'Resume' : 'Pause'}
            </PillButton>
          </div>

          <div className="game-toolbar__center">
            <div className="game-top-status" aria-live="polite">
              <span className="game-top-status__value">
                {currentFilled} / {totalNeeded}
              </span>
              {strict && (
                <span className="game-top-status__meta">✕ {mistakes}</span>
              )}
            </div>
          </div>

          <div className="game-toolbar__right">
            {canValidate && (
              <PillButton
                badge={
                  validationsRemaining !== Infinity
                    ? validationsRemaining
                    : undefined
                }
                disabled={validationsRemaining <= 0 || solved || paused}
                onClick={() => {
                  if (validationsRemaining <= 0 || solved || paused) return;
                  playClick();
                  validateGrid();
                }}
                aria-label="Validate grid"
              >
                Check
              </PillButton>
            )}
            {canUseHints && (
              <PillButton
                badge={
                  hintsRemaining !== Infinity ? hintsRemaining : undefined
                }
                isActive={touchMode === 'hint'}
                disabled={hintsRemaining <= 0 || solved || paused}
                onClick={() => {
                  if (hintsRemaining <= 0 || solved || paused) return;
                  if (touchMode !== 'hint') playModeSwitch();
                  setTouchMode('hint');
                }}
                aria-label="Hint mode"
                aria-pressed={touchMode === 'hint'}
              >
                Hint
              </PillButton>
            )}
          </div>
        </div>

        {/* Board */}
        <div className="game-board-wrapper">
          <BoardGrid
            ref={boardHandleRef}
            size={height}
            cols={width}
            inputMode={touchMode}
            rowClues={rowClues}
            colClues={colClues}
            cellSize={cellSize}
            grid={grid}
            completedRows={completedRows}
            completedCols={completedCols}
            rowClueDone={rowClueDone}
            colClueDone={colClueDone}
            errorCells={errorCells}
            animateErrors={false}
            markErrors={true}
            validFlash={validFlash}
            validFlashCells={validFlashCells}
            flashRows={emptyNumSet}
            flashCols={emptyNumSet}
            focusRow={focusRow}
            focusCol={focusCol}
            solved={solved}
            showSpanHints={showSpanHints}
            solution={puzzle.solution}
            lastAction={lastAction}
            particles={particles}
            gridRef={wrapperRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (focusRow < 0) {
                setFocusRow(0);
                setFocusCol(0);
              }
            }}
            onCellDown={(row, col, button) => {
              if (paused) return;
              const isRight = button === 2 || touchMode === 'mark';
              handleMouseDown(row, col, isRight);
            }}
            onCellMove={(row, col) => {
              if (!paused) handleMouseEnter(row, col);
            }}
            onCellUp={handleCellUp}
          />
          {paused && (
            <div
              className="game-paused-overlay"
              onClick={() => {
                playClick();
                setPaused(false);
              }}
            >
              <span className="game-paused-text">Paused</span>
              <span className="game-paused-sub">tap to continue</span>
            </div>
          )}
          {toast && (
            <div
              className={`game-toast${toast.includes('error') ? ' game-toast--error' : ''}`}
              aria-live="polite"
            >
              {toast}
            </div>
          )}
        </div>

        {/* Bottom toolbar */}
        <div
          className="game-toolbar game-toolbar--bottom game-toolbar--board-aligned"
          style={toolbarStyle}
        >
          <div className="game-toolbar__left">
            {onShare && (
              <button
                className="game-share-btn"
                onClick={() => {
                  playClick();
                  setShowSharePopup(true);
                }}
                aria-label="Share puzzle"
                disabled={solved}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="3" r="2" stroke="currentColor" strokeWidth="1.4" />
                  <circle cx="4" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
                  <circle cx="12" cy="13" r="2" stroke="currentColor" strokeWidth="1.4" />
                  <path
                    d="M5.8 7L10.2 4M5.8 9L10.2 12"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
          <div className="game-toolbar__center">
            <div
              className={`game-mode-toggle${
                paused ? ' game-mode-toggle--disabled' : ''
              }`}
              role="group"
              aria-label="Input mode"
              data-mode={touchMode}
            >
              <span className="game-mode-toggle__thumb" aria-hidden="true" />
              <button
                className={`game-mode-toggle__option game-mode-toggle__option--fill${
                  touchMode === 'fill' ? ' active' : ''
                }`}
                onClick={() => {
                  if (touchMode !== 'fill') playModeSwitch();
                  setTouchMode('fill');
                }}
                aria-label="Fill mode"
                disabled={paused}
              >
                <span
                  className="game-mode-toggle__fill-swatch"
                  aria-hidden="true"
                />
              </button>
              <button
                className={`game-mode-toggle__option game-mode-toggle__option--mark${
                  touchMode === 'mark' ? ' active' : ''
                }`}
                onClick={() => {
                  if (touchMode !== 'mark') playModeSwitch();
                  setTouchMode('mark');
                }}
                aria-label="Mark mode"
                disabled={paused}
              >
                {'\u2715'}
              </button>
            </div>
          </div>
          <div className="game-toolbar__right">
            <div
              className="game-footer-cluster game-footer-cluster--history"
              role="group"
              aria-label="History controls"
            >
              <button
                className="game-history-btn"
                onClick={() => {
                  playClick();
                  undo();
                }}
                aria-label="Undo"
                disabled={!canUndo}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 8L8 4M4 8L8 12M4 8H13C14.6569 8 16 9.3431 16 11V12C16 13.6569 14.6569 15 13 15H10"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                className="game-history-btn"
                onClick={() => {
                  playClick();
                  redo();
                }}
                aria-label="Redo"
                disabled={!canRedo}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M16 8L12 4M16 8L12 12M16 8H7C5.34315 8 4 9.3431 4 11V12C4 13.6569 5.34315 15 7 15H10"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                className="game-history-btn game-history-btn--clear"
                onClick={() => {
                  playClick();
                  reset();
                }}
                aria-label="Clear board"
                disabled={solved || paused}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 5H15M6 5V4C6 3.44772 6.44772 3 7 3H11C11.5523 3 12 3.44772 12 4V5M7 8V13M11 8V13M4 5L5 14C5 14.5523 5.44772 15 6 15H12C12.5523 15 13 14.5523 13 14L14 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* /game-board-area */}

      {/* Win popup */}
      {showWin && (
        <WinPopup
          title="Puzzle Solved!"
          solution={puzzle.solution}
          onClose={onBack}
          buttons={
            <>
              {onNext && (
                <Button variant="primary" size="wide-sm" onClick={onNext}>
                  {nextButtonText}
                </Button>
              )}
              <Button
                variant={onNext ? 'ghost' : 'primary'}
                size="wide-sm"
                onClick={onBack}
              >
                {backButtonText}
              </Button>
            </>
          }
        >
          <div className="win-time">{formatTime(elapsed)}</div>
          {hintsRemaining < 3 && (
            <Muted className="win-hints">
              Hints used: {3 - hintsRemaining}
            </Muted>
          )}
        </WinPopup>
      )}

      {/* Share popup */}
      {showSharePopup && onShare && (
        <SharePopup
          hasProgress={grid.some((row) => row.some((c) => c !== CellState.Empty))}
          onShare={async (includeProgress) => {
            const result = await onShare({
              solution: puzzle.solution,
              grid: includeProgress ? grid : undefined,
            });
            if (result === 'copied') {
              setToast('Link copied!');
              setTimeout(() => setToast(null), 1500);
            } else if (result === 'shared') {
              setToast('Shared!');
              setTimeout(() => setToast(null), 1500);
            }
            return result;
          }}
          onClose={() => setShowSharePopup(false)}
        />
      )}

      {/* Accessible grid */}
      <div
        role="grid"
        aria-label={`${name} - ${width} by ${height} picross puzzle`}
        className="sr-only"
      >
        {Array.from({ length: height }, (_, row) => (
          <div
            key={row}
            role="row"
            aria-label={`Row ${row + 1}, clues: ${rowClues[row].join(' ')}`}
          >
            {Array.from({ length: width }, (_, col) => {
              const label =
                grid[row][col] === CellState.Filled
                  ? 'filled'
                  : grid[row][col] === CellState.Marked
                  ? 'marked'
                  : 'empty';
              return (
                <span
                  key={col}
                  role="gridcell"
                  aria-label={`R${row + 1} C${col + 1}: ${label}`}
                >
                  {label}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );

  if (embedded) {
    return <div className="game-embedded">{gameContent}</div>;
  }

  return (
    <div className="game-container">
      <ParallaxBg className="game-bg" />

      <Title className="game-title">{name}</Title>

      {gameContent}
    </div>
  );
}
